import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import { 
    ChatBubbleLeftEllipsisIcon, 
    ClockIcon,
    ChevronRightIcon
} from '@heroicons/react/24/outline';

const AdminEnquiryManager = () => {
    const { get, patch } = useApi();
    const { showToast } = useAppState();
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('');
    const [selectedEnquiry, setSelectedEnquiry] = useState(null);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'details'
    const [adminNote, setAdminNote] = useState('');
    const [updating, setUpdating] = useState(false);

    const fetchEnquiries = useCallback(async () => {
        setLoading(true);
        try {
            const url = filterStatus ? `/api/enquiries/admin/list/?status=${filterStatus}` : '/api/enquiries/admin/list/';
            const data = await get(url);
            setEnquiries(data || []);
        } catch (err) {
            showToast('Failed to load enquiries', 'error');
        } finally {
            setLoading(false);
        }
    }, [filterStatus, get, showToast]);

    useEffect(() => {
        fetchEnquiries();
    }, [fetchEnquiries]);

    const handleUpdateStatus = async (id, newStatus) => {
        setUpdating(true);
        try {
            await patch(`/api/enquiries/admin/${id}/`, { status: newStatus, admin_note: adminNote });
            showToast(`Enquiry marked as ${newStatus}`);
            fetchEnquiries();
            if (selectedEnquiry?.id === id) {
                setSelectedEnquiry(prev => ({ ...prev, status: newStatus, admin_note: adminNote }));
            }
        } catch (err) {
            showToast('Update failed', 'error');
        } finally {
            setUpdating(false);
        }
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'New': return 'bg-amber-50 text-amber-600 border-amber-100';
            case 'In Progress': return 'bg-indigo-50 text-indigo-600 border-indigo-100';
            case 'Resolved': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
            default: return 'bg-slate-50 text-slate-600 border-slate-100';
        }
    };

    const handleSelectEnquiry = (enquiry) => {
        setSelectedEnquiry(enquiry);
        setAdminNote(enquiry.admin_note || '');
        setViewMode('details');
    };

    const handleBackToList = () => {
        setViewMode('list');
    };

    return (
        <div className="min-h-screen bg-slate-50/30 w-full mt-[-2rem]">
            <div className="max-w-7xl mx-auto">
                <React.Fragment>
                    {/* Header Section */}
                {viewMode === 'list' && (
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-2 animate-in fade-in slide-in-from-top-4 duration-700">
                        <div>
                            <div className="flex items-center gap-2 text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-2">
                                <ChatBubbleLeftEllipsisIcon className="w-4 h-4" />
                                <span>Feedback Hub Management</span>
                            </div>
                            <h1 className="text-4xl font-black text-[#18216D] tracking-tight uppercase">Enquiries & Feedback</h1>
                            <p className="text-slate-400 font-bold text-sm mt-1">Reviewing and acting upon institutional feedback & enquiries.</p>
                        </div>

                        <div className="flex items-center gap-2 bg-white p-2 rounded-[1.5rem] shadow-sm border border-slate-100">
                            {['', 'New', 'In Progress', 'Resolved'].map((s) => (
                                <button
                                    key={s}
                                    onClick={() => setFilterStatus(s)}
                                    className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                                        filterStatus === s 
                                        ? 'bg-[#18216D] text-white shadow-lg shadow-indigo-900/20' 
                                        : 'text-slate-400 hover:text-[#18216D] hover:bg-slate-50'
                                    }`}
                                >
                                    {s || 'All Items'}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* List View - Card Grid */}
                {viewMode === 'list' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in zoom-in duration-500">
                        {loading ? (
                            Array(6).fill(0).map((_, i) => (
                                <div key={i} className="bg-white rounded-[2.5rem] h-64 animate-pulse border border-slate-100" />
                            ))
                        ) : enquiries.length === 0 ? (
                            <div className="col-span-full bg-white rounded-[3rem] p-24 text-center border border-slate-100 border-dashed">
                                <div className="w-20 h-20 bg-slate-50 rounded-3xl flex items-center justify-center mx-auto mb-6 text-slate-200">
                                    <ClockIcon className="w-10 h-10" />
                                </div>
                                <h3 className="text-lg font-black text-[#18216D] uppercase tracking-[0.2em] italic">Quiet on the front</h3>
                                <p className="text-slate-400 font-bold text-sm mt-2 uppercase tracking-widest">No enquiries currently require your attention in this category.</p>
                            </div>
                        ) : (
                            enquiries.map((e, idx) => {
                                const profile = e.sender_profile || {};
                                const roleColors = {
                                    'Student': 'bg-amber-400 text-[#18216D]',
                                    'Teacher': 'bg-emerald-400 text-emerald-950',
                                    'Parent': 'bg-indigo-400 text-white',
                                    'External': 'bg-slate-400 text-white'
                                };

                                return (
                                    <button
                                        key={e.id}
                                        onClick={() => handleSelectEnquiry(e)}
                                        style={{ animationDelay: `${idx * 50}ms` }}
                                        className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.02)] hover:shadow-[0_20px_50px_rgba(24,33,109,0.08)] hover:-translate-y-1 transition-all duration-300 group text-left relative overflow-hidden flex flex-col h-full"
                                    >
                                        {/* Category Accent Bar */}
                                        <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${
                                            e.category.includes('Technical') ? 'bg-amber-400' : 
                                            e.category.includes('Academic') ? 'bg-indigo-500' : 
                                            e.category.includes('Billing') ? 'bg-emerald-500' : 'bg-slate-300'
                                        }`} />
                                        
                                        <div className="flex justify-between items-start mb-6 relative z-10">
                                            <div className={`px-4 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest border ${getStatusStyle(e.status)}`}>
                                                {e.status}
                                            </div>
                                            <div className={`px-2 py-1 rounded-lg text-[8px] font-black uppercase tracking-tighter ${roleColors[profile.role] || roleColors.External}`}>
                                                {profile.role || 'Guest'}
                                            </div>
                                        </div>

                                        <div className="flex-1 relative z-10">
                                            <p className="text-[9px] font-black text-indigo-500 uppercase tracking-[0.3em] mb-2">{e.category}</p>
                                            <h3 className="text-xl font-black text-[#18216D] tracking-tight uppercase leading-tight mb-4 group-hover:text-indigo-600 transition-colors line-clamp-2">
                                                {e.subject}
                                            </h3>
                                            <p className="text-slate-500 text-xs font-bold line-clamp-2 leading-relaxed opacity-70 italic">
                                                "{e.message}"
                                            </p>
                                        </div>

                                        <div className="mt-8 pt-6 border-t border-slate-50 flex items-center justify-between relative z-10">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-[#18216D] font-black text-sm uppercase group-hover:bg-indigo-50 transition-colors shadow-sm">
                                                    {e.full_name.charAt(0)}
                                                </div>
                                                <div className="overflow-hidden">
                                                    <p className="text-[11px] font-black text-[#18216D] uppercase tracking-tight truncate">{e.full_name}</p>
                                                    <p className="text-[9px] font-bold text-slate-400 italic truncate">{e.email}</p>
                                                </div>
                                            </div>
                                            <div className="w-8 h-8 rounded-full border border-slate-100 flex items-center justify-center text-slate-300 group-hover:text-indigo-500 group-hover:border-indigo-100 transition-all">
                                                <ChevronRightIcon className="w-4 h-4 translate-x-0 group-hover:translate-x-0.5 transition-transform" />
                                            </div>
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>
                )}

                {/* Detail View - Redesigned Workspace */}
                {viewMode === 'details' && selectedEnquiry && (
                    <div className="animate-in fade-in slide-in-from-bottom-8 duration-700">
                        {/* Navigation Sub-header */}
                        <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-100">
                            <button 
                                onClick={handleBackToList}
                                className="group flex items-center gap-3 text-slate-400 hover:text-[#18216D] transition-colors"
                            >
                                <div className="w-8 h-8 rounded-xl bg-white border border-slate-100 flex items-center justify-center group-hover:bg-indigo-50 group-hover:border-indigo-100 shadow-sm transition-all">
                                    <i className="fas fa-arrow-left text-[10px]"></i>
                                </div>
                                <span className="text-[10px] font-black uppercase tracking-[0.2em]">Return to Inbox</span>
                            </button>

                            <div className="flex items-center gap-4">
                                <div className={`px-4 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest border ${getStatusStyle(selectedEnquiry.status)}`}>
                                    {selectedEnquiry.status}
                                </div>
                                <div className="h-4 w-px bg-slate-200 mx-2" />
                                <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Case ID: <span className="text-slate-500">#{selectedEnquiry.id.toString().padStart(6, '0')}</span></p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                            {/* Main Context - 8 Columns */}
                            <div className="lg:col-span-8 space-y-6">
                                <div className="bg-white rounded-[2rem] p-10 shadow-[0_8px_40px_rgba(24,33,109,0.03)] border border-slate-100">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-1.5 h-1.5 rounded-full bg-[#FFC425]" />
                                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.3em]">{selectedEnquiry.category}</span>
                                    </div>

                                    <h2 className="text-3xl font-black text-[#18216D] tracking-tight uppercase leading-snug mb-8">
                                        {selectedEnquiry.subject}
                                    </h2>

                                    <div className="text-slate-600 font-bold text-sm leading-relaxed whitespace-pre-wrap py-8 border-t border-slate-50 italic">
                                        "{selectedEnquiry.message}"
                                    </div>

                                    <div className="mt-8 pt-8 border-t border-slate-50 grid grid-cols-2 gap-8">
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shadow-sm">
                                                <i className="fas fa-clock text-sm"></i>
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 text-opacity-70">Enquiry Initiation</p>
                                                <p className="text-[11px] font-extrabold text-[#18216D] uppercase">{new Date(selectedEnquiry.created_at).toLocaleString()}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-600 shadow-sm">
                                                <i className="fas fa-calendar-check text-sm"></i>
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 text-opacity-70">Interaction Sync</p>
                                                <p className="text-[11px] font-extrabold text-[#18216D] uppercase">{new Date(selectedEnquiry.updated_at).toLocaleString()}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Resolution Workspace */}
                                <div className="bg-white rounded-[2rem] p-10 border border-slate-100 shadow-sm relative overflow-hidden">
                                     <div className="absolute top-0 right-0 w-32 h-32 bg-slate-50 rounded-full -mr-16 -mt-16 pointer-events-none opacity-50" />
                                     <div className="flex items-center gap-4 mb-8">
                                        <div className="w-10 h-10 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-[#18216D]">
                                            <i className="fas fa-pen-nib text-sm"></i>
                                        </div>
                                        <h3 className="text-[11px] font-black text-[#18216D] uppercase tracking-widest">Internal Resolution Notes</h3>
                                    </div>

                                    <textarea
                                        rows="4"
                                        value={adminNote}
                                        onChange={(e) => setAdminNote(e.target.value)}
                                        placeholder="Document your findings or internal resolution steps..."
                                        className="w-full px-8 py-6 bg-slate-50 border-2 border-transparent rounded-[2rem] text-sm font-bold text-[#18216D] placeholder-slate-300 focus:outline-none focus:ring-0 focus:border-indigo-100 focus:bg-white transition-all font-sans resize-none shadow-inner mb-6"
                                    />

                                    <div className="flex items-center gap-4">
                                        <button 
                                            onClick={() => handleUpdateStatus(selectedEnquiry.id, 'In Progress')}
                                            disabled={updating || selectedEnquiry.status === 'In Progress'}
                                            className="px-8 py-4 bg-white border border-slate-200 text-[#18216D] font-black text-[10px] uppercase tracking-widest rounded-2xl hover:border-indigo-400 hover:bg-slate-50 transition-all disabled:opacity-30 disabled:pointer-events-none active:scale-95 shadow-sm"
                                        >
                                            Update Internal State
                                        </button>
                                        <button 
                                            onClick={() => handleUpdateStatus(selectedEnquiry.id, 'Resolved')}
                                            disabled={updating || selectedEnquiry.status === 'Resolved'}
                                            className="flex-1 px-8 py-4 bg-[#18216D] text-white font-black text-[10px] uppercase tracking-widest rounded-2xl hover:bg-[#1e2985] transition-all shadow-xl shadow-indigo-900/10 disabled:opacity-30 flex items-center justify-center gap-3 active:scale-95"
                                        >
                                            {updating ? (
                                                <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                            ) : <i className="fas fa-check-double text-xs"></i>}
                                            {updating ? 'Synchronizing...' : 'Finalize & Archive Case'}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Info Sidebar - 4 Columns */}
                            <div className="lg:col-span-4 space-y-6">
                                {/* Enhanced User Profile Card */}
                                <div className="bg-[#18216D] rounded-[2rem] p-10 text-white shadow-2xl shadow-indigo-900/20 relative overflow-hidden group">
                                    {/* Glassmorphism Background Accent */}
                                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />
                                    
                                    <div className="relative z-10 text-center">
                                        <div className="w-24 h-24 bg-white/10 backdrop-blur-md rounded-[2.5rem] border border-white/20 flex items-center justify-center text-4xl font-black mx-auto mb-6 shadow-2xl relative">
                                            {selectedEnquiry.full_name.charAt(0)}
                                            <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-xl bg-[#FFC425] flex items-center justify-center text-[#18216D] text-xs shadow-lg">
                                                <i className={`fas ${
                                                    selectedEnquiry.sender_profile?.role === 'Student' ? 'fa-graduation-cap' :
                                                    selectedEnquiry.sender_profile?.role === 'Teacher' ? 'fa-chalkboard-user' :
                                                    'fa-user'
                                                }`}></i>
                                            </div>
                                        </div>
                                        <h4 className="text-xl font-black uppercase tracking-tight mb-1 leading-tight">{selectedEnquiry.full_name}</h4>
                                        <p className="text-[10px] font-black text-indigo-200 uppercase tracking-widest mb-8 opacity-60">{selectedEnquiry.email}</p>
                                        
                                        <div className="space-y-3 border-t border-white/10 pt-8 text-left">
                                            {/* Role Badge */}
                                            <div className="flex justify-between items-center bg-white/5 p-3 rounded-xl border border-white/5">
                                                <span className="text-[8px] font-black uppercase tracking-[0.2em] text-white/40">Affiliation</span>
                                                <span className="text-[9px] font-black uppercase bg-[#FFC425] text-[#18216D] px-3 py-1 rounded-lg">
                                                    {selectedEnquiry.sender_profile?.role || 'Guest'}
                                                </span>
                                            </div>

                                            {/* Role Specific Metadata */}
                                            {selectedEnquiry.sender_profile?.role === 'Student' && (
                                                <>
                                                    <div className="flex justify-between items-center bg-white/5 p-3 rounded-xl">
                                                        <span className="text-[8px] font-black uppercase tracking-[0.2em] text-white/40">Grade Level</span>
                                                        <span className="text-[10px] font-black text-amber-200">{selectedEnquiry.sender_profile.grade}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center bg-white/5 p-3 rounded-xl">
                                                        <span className="text-[8px] font-black uppercase tracking-[0.2em] text-white/40">Stream Unit</span>
                                                        <span className="text-[10px] font-black text-amber-200">{selectedEnquiry.sender_profile.stream}</span>
                                                    </div>
                                                </>
                                            )}

                                            {selectedEnquiry.sender_profile?.role === 'Teacher' && (
                                                <>
                                                    <div className="flex justify-between items-center bg-white/5 p-3 rounded-xl">
                                                        <span className="text-[8px] font-black uppercase tracking-[0.2em] text-white/40">Registry ID</span>
                                                        <span className="text-[10px] font-black text-emerald-300">{selectedEnquiry.sender_profile.id}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center bg-white/5 p-3 rounded-xl">
                                                        <span className="text-[8px] font-black uppercase tracking-[0.2em] text-white/40">Contact</span>
                                                        <span className="text-[10px] font-black text-emerald-300">{selectedEnquiry.sender_profile.phone}</span>
                                                    </div>
                                                </>
                                            )}

                                            {selectedEnquiry.sender_profile?.role === 'Parent' && (
                                                <>
                                                    <div className="flex justify-between items-center bg-white/5 p-3 rounded-xl">
                                                        <span className="text-[8px] font-black uppercase tracking-[0.2em] text-white/40">Contact</span>
                                                        <span className="text-[10px] font-black text-indigo-200">{selectedEnquiry.sender_profile.phone}</span>
                                                    </div>
                                                    
                                                    {/* Parent Children Accordion */}
                                                    <div className="bg-white/5 rounded-xl overflow-hidden mt-4 border border-white/5">
                                                        <button 
                                                            onClick={() => document.getElementById('child-accordion').classList.toggle('hidden')}
                                                            className="w-full flex justify-between items-center p-3 text-[9px] font-black uppercase tracking-widest hover:bg-white/5 transition-colors"
                                                        >
                                                            <span>Linked Children</span>
                                                            <div className="flex items-center gap-2">
                                                                <span className="bg-indigo-500/50 px-2 py-0.5 rounded-full">{selectedEnquiry.sender_profile.children.length}</span>
                                                                <i className="fas fa-chevron-down text-[8px] opacity-40"></i>
                                                            </div>
                                                        </button>
                                                        <div id="child-accordion" className="hidden border-t border-white/5 p-3 space-y-2">
                                                            {selectedEnquiry.sender_profile.children.map((child, i) => (
                                                                <div key={i} className="flex items-center gap-2 text-[10px] font-bold text-indigo-100/70">
                                                                    <div className="w-1 h-1 rounded-full bg-indigo-400" />
                                                                    {child}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Timeline Visualization */}
                                <div className="bg-white rounded-[2rem] p-10 border border-slate-100 shadow-sm relative group overflow-hidden">
                                     <div className="absolute top-0 right-0 w-32 h-32 bg-slate-50/50 rounded-full -mr-16 -mt-16 pointer-events-none" />
                                     <h5 className="text-[10px] font-black text-[#18216D] uppercase tracking-[0.3em] mb-8 flex items-center gap-3 relative z-10">
                                        <span className="w-1.5 h-1.5 bg-[#FFC425] rounded-full"></span> Lifespan Timeline
                                    </h5>
                                    
                                    <div className="space-y-8 relative before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-px before:bg-slate-100 relative z-10">
                                        <div className="relative pl-8">
                                            <div className="absolute left-0 top-1 w-[22px] h-[22px] bg-white border-2 border-emerald-400 rounded-full flex items-center justify-center z-10 shadow-sm group-hover:scale-110 transition-transform">
                                                <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                                            </div>
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Receipt Initiation</p>
                                            <p className="text-[10px] font-extrabold text-[#18216D]">{new Date(selectedEnquiry.created_at).toLocaleDateString()}</p>
                                        </div>

                                        <div className="relative pl-8">
                                            <div className="absolute left-0 top-1 w-[22px] h-[22px] bg-white border-2 border-indigo-400 rounded-full flex items-center justify-center z-10 shadow-sm group-hover:scale-110 transition-transform">
                                                <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full" />
                                            </div>
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Current Standing</p>
                                            <p className="text-[10px] font-extrabold text-[#18216D] uppercase">{selectedEnquiry.status}</p>
                                        </div>

                                        <div className="relative pl-8 opacity-40 grayscale group-hover:grayscale-0 group-hover:opacity-100 transition-all">
                                            <div className="absolute left-0 top-1 w-[22px] h-[22px] bg-white border-2 border-slate-200 rounded-full flex items-center justify-center z-10 shadow-sm">
                                                <div className="w-1.5 h-1.5 bg-slate-200 rounded-full" />
                                            </div>
                                            <p className="text-[9px] font-black text-slate-300 uppercase tracking-widest mb-1">Projected Resolution</p>
                                            <p className="text-[10px] font-extrabold text-slate-300 uppercase">Awaiting Action</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    )}
                </React.Fragment>
            </div>
        </div>
    );
};

export default AdminEnquiryManager;
